import React, { useEffect, useState, useCallback } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { connect } from 'unistore/full/react';
import actions from './actions';
import Content from './Content';
import './Search.scss';
import HubView from './HubView';
import spfetch from './spfetch';
import NavigationBar from './FigmaComponents/NavigationBar';
import getTypeFromUri from './get-type-from-uri';
import getHubItemUri from './get-hub-item-uri';

export default connect(
  ['searchesBySectionAndString', 'searchItems'],
  actions
)(
  React.memo(function Search(props) {
    const history = useHistory();
    const { section, searchString } = useRouteMatch()?.params;

    const { searchesBySectionAndString, searchItems } = props;
    const [isSearchFieldVisible, setIsSearchFieldVisible] = useState(
      !!searchString
    );
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const isSearching = isSearchFocused || !!searchString;
    const [deferredSearchString, setDeferredSearchString] = useState(
      searchString
    );

    const setSearchString = (searchString) =>
      history.replace(`/search/${section ? `${section}/` : ''}${searchString}`);

    const onSearch = useCallback(
      (event) => {
        const newSearchString = event?.target?.value;
        setSearchString(newSearchString);
        if (newSearchString) props.search(newSearchString, section);
      },
      [section]
    );

    useEffect(() => {
      if (
        !searchString ||
        (section in searchesBySectionAndString &&
          searchString in searchesBySectionAndString[section])
      )
        setDeferredSearchString(searchString);
      else {
        let timeout = setTimeout(() => {
          setDeferredSearchString(searchString);
        }, 300);
        return () => clearTimeout(timeout);
      }
    }, [searchString]);

    useEffect(() => {
      if (searchString) onSearch({ target: { value: searchString } });
    }, [section]);

    const searchResponse = !!searchString
      ? searchesBySectionAndString[section]?.[deferredSearchString]
      : null;

    const { playlists, stations } = props;
    return (
      <div className="Content">
        <div className="Search">
          {isSearchFieldVisible ? (
            <>
              {section ? (
                <NavigationBar />
              ) : (
                <div className="Search__header-search-wrapper">
                  <div className="Search__header-search-field-wrapper">
                    <input
                      type="text"
                      className="Search__header-search-field-input"
                      placeholder="Search"
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      onChange={onSearch}
                      value={searchString}
                      autoFocus
                    />

                    <div
                      className={`Search__header-search-field-cancel ${
                        isSearching
                          ? 'Search__header-search-field-cancel--is-searching'
                          : ''
                      }`}
                      onPointerDown={() => {
                        setSearchString('');
                        setIsSearchFieldVisible(false);
                      }}
                    >
                      Cancel
                    </div>

                    <div
                      className={`Search__header-search-field-cross-wrapper ${
                        isSearching
                          ? 'Search__header-search-field-cross-wrapper--is-searching'
                          : ''
                      }`}
                      onClick={() => setSearchString('')}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 16 16"
                        className="Search__header-search-field-cross"
                      >
                        <path
                          fill="#fff"
                          d="M14.354 2.353l-.708-.707L8 7.293 2.353 1.646l-.707.707L7.293 8l-5.647 5.646.707.708L8 8.707l5.646 5.647.708-.708L8.707 8l5.647-5.647z"
                        ></path>
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              {section || searchString ? (
                <HubView
                  data={searchResponse}
                  onNavigate={(item) => {
                    if (getTypeFromUri(getHubItemUri(item)) === 'track') {
                      props.playTracksFromUri({
                        rows: [{ track: { uri: getHubItemUri(item) } }],
                      });
                    } else {
                      setSearchString('');
                    }
                    if (!item?.target?.uri.startsWith('spotify:search:')) {
                      props.saveSearchItem(item);
                    }
                  }}
                />
              ) : searchItems.length ? (
                <div className="Search__recent-searches">
                  <div className="Search__recent-searches-title">
                    Recent searches
                  </div>

                  <HubView
                    data={{ body: searchItems }}
                    overrides={{
                      action: (item) => (
                        <g
                          onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            props.removeSearchItem(item);
                          }}
                        >
                          <rect
                            width="44"
                            height="44"
                            fill="#000"
                            x="330"
                            y="10"
                            fillOpacity="0.001"
                          />
                          <path
                            fill="#BDBDBD"
                            d="M357.354 26.353l-.708-.707L351 31.293l-5.647-5.647-.707.707L350.293 32l-5.647 5.646.707.708L351 32.707l5.646 5.647.708-.708L351.707 32l5.647-5.647z"
                          ></path>
                        </g>
                      ),
                    }}
                  />

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="197"
                    height="28"
                    fill="none"
                    viewBox="0 0 197 28"
                    style={{ margin: '24px auto 0' }}
                    onClick={props.clearSearchItems}
                  >
                    <rect
                      width="195"
                      height="26"
                      stroke="#535353"
                      rx="15"
                      x="1"
                      y="1"
                    />
                    <text
                      fill="#fff"
                      style={{ whiteSpace: 'pre' }}
                      fontSize="10"
                      fontWeight="bold"
                      letterSpacing=".1em"
                    >
                      <tspan x="24.262" y="17.275">
                        CLEAR RECENT SEARCHES
                      </tspan>
                    </text>
                  </svg>
                </div>
              ) : (
                <div className="Search__info">
                  <div className="Search__info-title">Play what you love</div>
                  <div className="Search__info-subtitle">
                    Search for artists, songs, podcasts and more.
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="Search__title">Search</div>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="375"
                height="44"
                fill="none"
                viewBox="0 0 375 44"
                className="Search__field"
                onClick={() => setIsSearchFieldVisible(true)}
              >
                <path
                  fill="#fff"
                  d="M16 4a4 4 0 014-4h335a4 4 0 014 4v36a4 4 0 01-4 4H20a4 4 0 01-4-4V4z"
                ></path>
                <path
                  fill="#181818"
                  d="M338.001 18.5L338 21.753c0 3.996-3.251 7.247-7.247 7.247h-.508c-3.992 0-7.24-3.246-7.245-7.237V18.5h-1V22c.13 4.339 3.66 7.857 7.999 7.987V33h1v-3.013c4.339-.13 7.871-3.648 8.002-7.987v-3.5h-1zm-7.501 7c2.475 0 4.5-2.025 4.5-4.5v-5c0-2.475-2.025-4.5-4.5-4.5A4.513 4.513 0 00326 16v5a4.512 4.512 0 004.5 4.5zm-3.501-9.5c0-1.93 1.57-3.5 3.5-3.5S334 14.07 334 16v5c0 1.93-1.57 3.5-3.5 3.5S327 22.93 327 21v-5h-.001z"
                ></path>
                <text
                  fill="#181818"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="16"
                  letterSpacing="0em"
                >
                  <tspan x="64" y="27.24">
                    Artists, songs, or podcasts
                  </tspan>
                </text>
                <path
                  fill="#181818"
                  d="M48.387 26.623A8.47 8.47 0 0051 20.5a8.5 8.5 0 10-8.5 8.5 8.454 8.454 0 005.125-1.73l4.401 5.153.76-.649-4.399-5.151zM42.5 28c-4.136 0-7.5-3.364-7.5-7.5s3.364-7.5 7.5-7.5 7.5 3.364 7.5 7.5-3.364 7.5-7.5 7.5z"
                ></path>
              </svg>

              <HubView
                url="https://spclient.wg.spotify.com/hubview-mobile-v1/browse"
                noTitle
                style={{ margin: '16px' }}
              />
            </>
          )}
        </div>
      </div>
    );
  })
);
